'use client'

import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

/**
 * One pressable choice — the creator's whole vocabulary for "pick this".
 *
 * The screen had four spellings of the same control: the variant chips on a
 * line, the variant chips on a suggestion (a second, near-identical copy),
 * the hour dropdown, and the rental ladder's rows. A person cannot learn a
 * control they meet in four costumes, so every bounded choice on the order
 * screen is now this button, and anything that is NOT this button is a genuinely
 * different kind of answer — a date, a paragraph, a link.
 *
 * `aria-pressed` rather than `aria-selected` or a radio: these are toggles a
 * screen reader should announce as pressed or not, and several of them (the
 * variant row) really do clear on a second press.
 */
export function ChoiceChip({
  pressed,
  onClick,
  children,
  note,
  disabled,
  className,
  title,
}: {
  pressed: boolean
  onClick: () => void
  children: ReactNode
  /** The quiet half — a price, a rate, a saving. Reads under the label on a phone. */
  note?: ReactNode
  disabled?: boolean
  className?: string
  title?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      {...(title ? { title } : {})}
      className={cn(
        // 44px minimum on both axes: these replaced a dropdown whose options
        // were full-width rows, and a grid of chips is only an improvement if
        // each one is still a target.
        'inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-4 py-2 text-[0.8rem] font-semibold transition',
        'focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none',
        disabled
          ? 'cursor-not-allowed border border-white/10 text-white/30'
          : pressed
            ? 'bg-[image:var(--flou-gradient-solid)] text-white'
            : 'border border-white/15 text-white/70 hover:border-white/40 hover:text-white',
        className,
      )}
    >
      <span className="truncate">{children}</span>
      {note ? (
        <span className={cn('shrink-0 font-medium', pressed ? 'text-white/75' : 'text-white/55')}>
          {note}
        </span>
      ) : null}
    </button>
  )
}
